import axios, { AxiosRequestConfig } from 'axios';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { syncRememberedSession } from '../../utils/authStorage';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:7002/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased timeout to 60 seconds for slow database queries
  headers: {
    'Content-Type': 'application/json'
  }
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors and token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = sessionStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No refresh token available, redirect to login
        console.error('🔒 No refresh token available, redirecting to login');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the token
        console.log('🔄 Attempting to refresh access token...');
        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken
        });

        if (response.data?.token) {
          const newToken = response.data.token;
          const newRefreshToken = response.data.refreshToken;

          // Store new tokens
          sessionStorage.setItem('token', newToken);
          syncRememberedSession(); // keep a remembered session from holding a stale token
          if (newRefreshToken) {
            sessionStorage.setItem('refreshToken', newRefreshToken);
          }

          console.log('✅ Token refreshed successfully');

          // Update the failed request with new token and retry
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);

          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        processQueue(refreshError, null);
        toast.error('Session expired. Please log in again.');

        // Clear session and redirect to login
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('refreshToken');
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Automatically display toast messages for non-401 API failures
    if (error.response?.status !== 401) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred';
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

// Legacy API wrappers. The generic <T> lets callers opt in to strongly typed
// responses (e.g. `getAPI<LoginResponse>('/auth/login')`) while existing
// untyped call sites keep working under the default `any`. Switch the default
// to `unknown` once call sites adopt the generic — that conversion is tracked
// as Phase 2j follow-up.
export const getAPI = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.get<T>(url, config);
  return response.data as T;
};

export const postAPI = async <T = any, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.post<T>(url, data, config);
  return response.data as T;
};

export const putAPI = async <T = any, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.put<T>(url, data, config);
  return response.data as T;
};

export const deleteAPI = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.delete<T>(url, config);
  return response.data as T;
};

// Additional API functions for backward compatibility
export const useFetchData = (onSuccess: (data: any) => void, onError: (data: any) => void, url: string, key: string) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let timeoutId: any;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // For /companies/users endpoint specifically, use optimized timeout
        // Query has been optimized on backend with direct SQL
        if (url.includes('/companies/users')) {
          // Set a reasonable timeout for the optimized endpoint
          const controller = new AbortController();
          timeoutId = setTimeout(() => {
            controller.abort();
          }, 15000); // 15 second timeout for users endpoint (optimized query)

          try {
            const response = await apiClient.get(url, { signal: controller.signal });
            clearTimeout(timeoutId);
            const responseData = response.data;
            setData(responseData);
            onSuccess(responseData);
          } catch (err: any) {
            clearTimeout(timeoutId);
            throw err;
          }
        } else {
          // Normal timeout for other endpoints
          const response = await apiClient.get(url);
          const responseData = response.data;
          setData(responseData);
          onSuccess(responseData);
        }
      } catch (err: any) {
        console.error(`Error fetching data from ${url}:`, err);

        // Provide user-friendly error messages
        let errorMessage = 'Failed to fetch data';
        if (err.code === 'ERR_NETWORK') {
          errorMessage = 'Cannot connect to server. Please ensure the backend server is running on port 7002.';
        } else if (err.code === 'ECONNABORTED' || err.name === 'AbortError' || err.name === 'CanceledError') {
          errorMessage = 'Request timed out. The database query is taking too long. This is a known performance issue.';
        } else if (err.response?.status === 401) {
          errorMessage = 'Authentication failed. Your session may have expired. Please log in again.';
        } else if (err.response?.status === 404) {
          errorMessage = 'Endpoint not found. Please check the API route.';
        } else if (err.response?.status === 403) {
          errorMessage = 'Access denied. You do not have permission to view this data.';
        } else if (err.response?.status) {
          errorMessage = `Server error: ${err.response.status}`;
        }

        console.warn(errorMessage);
        setError({ ...err, message: errorMessage });
        onError({ ...err, message: errorMessage });

        // Set empty data structure on error so UI doesn't break
        setData({ status: false, message: [], error: errorMessage });
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, key, retryCount]); // Removed onSuccess, onError from dependencies to prevent infinite loop

  return {
    data,
    isLoading: loading,
    isError: !!error,
    error,
    refetch: () => setRetryCount(prev => prev + 1)
  };
};

export const useAddData = (url: string) => {
  const [isLoading, setIsLoading] = useState(false);

  return {
    mutate: async (data: any) => {
      try {
        setIsLoading(true);
        console.log('🔄 useAddData - Submitting to:', url, 'with data:', data);

        // Extract custom handlers if provided
        const { setOpen, formik: resetForm, setSubmitting, url: customUrl, ...actualData } = data;

        // Determine the actual API endpoint
        let apiEndpoint = url;
        if (customUrl) {
          // If a custom URL is provided in data, use it
          apiEndpoint = customUrl;
        }

        // Normalize endpoint - remove any leading /api/ since baseURL already has it
        apiEndpoint = apiEndpoint.replace(/^\/api\//, '').replace(/^api\//, '');

        // Add leading slash if not present
        if (!apiEndpoint.startsWith('/')) {
          apiEndpoint = `/${apiEndpoint}`;
        }

        // Make sure it ends with 's' for REST convention (customer -> customers)
        if (apiEndpoint === '/customer') {
          apiEndpoint = '/customers';
        }

        console.log('📡 Final API endpoint:', apiEndpoint);

        const response = await postAPI(apiEndpoint, actualData);

        console.log('✅ useAddData - Response:', response);

        // Call success handlers if provided
        if (setSubmitting) setSubmitting(false);
        if (setOpen) setOpen();
        if (resetForm) resetForm();

        // Show success message if available
        if (response?.message) {
          console.log('✨ Success:', response.message);
        }

        setIsLoading(false);
        return response;
      } catch (error: any) {
        console.error('❌ useAddData - Error:', error);
        setIsLoading(false);

        // Call error handler if provided
        if (data.setSubmitting) data.setSubmitting(false);

        throw error;
      }
    },
    isLoading
  };
};

export const useSingleData = (url: string) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const mutate = async (params?: any) => {
    try {
      setLoading(true);
      setError(null);
      let result;
      if (params && typeof params === 'object' && params.id) {
        // Fetch single record by ID using GET
        const fetchUrl = params.url || url;
        const normalizedUrl = fetchUrl.replace(/^\/api\//, '').replace(/^api\//, '');
        result = await getAPI(`${normalizedUrl}/${params.id}`);
      } else if (params) {
        // POST with data
        result = await postAPI(url, params);
      } else {
        result = await getAPI(url);
      }
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    mutate,
    data,
    loading,
    error,
    refetch: () => mutate()
  };
};

export const useFetchOwnData = (url: string, productId?: any, key?: string) => {
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Normalize URL - remove /api/ prefix if present since baseURL already includes it
        let fetchUrl = url.replace(/^\/api\//, '').replace(/^api\//, '');
        if (!fetchUrl.startsWith('/')) {
          fetchUrl = `/${fetchUrl}`;
        }
        // Append productId as query param if provided
        if (productId) {
          const separator = fetchUrl.includes('?') ? '&' : '?';
          fetchUrl = `${fetchUrl}${separator}product_id=${productId}`;
        }
        const response = await apiClient.get(fetchUrl);
        const responseData = response.data;
        setData(responseData?.message || responseData?.data || responseData || []);
      } catch (err: any) {
        console.error(`Error in useFetchOwnData for ${url}:`, err);
        setError(err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, productId, key]);

  return {
    data,
    loading,
    error,
    refetch: async () => {
      try {
        let fetchUrl = url.replace(/^\/api\//, '').replace(/^api\//, '');
        if (!fetchUrl.startsWith('/')) fetchUrl = `/${fetchUrl}`;
        if (productId) {
          const separator = fetchUrl.includes('?') ? '&' : '?';
          fetchUrl = `${fetchUrl}${separator}product_id=${productId}`;
        }
        const response = await apiClient.get(fetchUrl);
        setData(response.data?.message || response.data?.data || response.data || []);
      } catch (err) {
        console.error('Refetch error:', err);
      }
    }
  };
};

export const singleAPI = async <T = any>(url: string, data?: unknown): Promise<T> => {
  // If data is a simple ID (string/number), fetch by ID using GET
  if (data !== undefined && data !== null) {
    if (typeof data === 'string' || typeof data === 'number') {
      // GET request: /url/id — fetch a single record
      return await getAPI<T>(`${url}/${data}`);
    }
    // If data is an object, POST it
    return await postAPI<T>(url, data);
  }
  return await getAPI<T>(url);
};

export default apiClient;